import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import minMax from 'dayjs/plugin/minMax';
import { isNumber } from 'lodash-es';
import { storeToRefs } from 'pinia';
import slugify from 'slugify';
import { computed, ref } from 'vue';
import { useCommonStore } from '~/common/stores/common.store.js';
import { htmlToText } from '~/common/utils/common.utils.js';
import { useColumnTemplates } from '~/project-management/composables/pm-column-templates.composable.js';
import { useProjectManagementStore } from '~/project-management/store/pm.store';

dayjs.extend(minMax);
dayjs.extend(isSameOrBefore);

// Colors mirror the app's Gantt bar CSS, but toned down for full-cell fills:
//   - base     = a lighter shade for the unfilled (remaining) portion of the bar
//   - progress = the app's actual bar color for the filled (done) portion
//   - border   = a slightly darker shade drawn around the bar outline
//
//   App CSS reference:
//     Normal task  bg-primary-400 (#53B1FD)  / progress bg-primary-600 (#1570EF)
//     WBS          bg-orange-300  (#FDBA74)  / progress bg-orange-400  (#FB923C)
//     Critical     bg-error-500   (#F04438)  / progress bg-error-600   (#D92D20)
//     Completed    bg-success-400 (#32D583)
const GANTT_COLORS = {
  // primary-200 (base) / primary-500 (progress) / primary-400 (border)
  task: { base: 'FFB2DDFF', progress: 'FF2E90FA', border: 'FF53B1FD' },
  // orange-100 (base) / orange-300 (progress, = app bar) / orange-400 (border)
  wbs: { base: 'FFFFEDD5', progress: 'FFFDBA74', border: 'FFFB923C' },
  // error-100 (base) / error-400 (progress) / error-500 (border, = app bar)
  critical: { base: 'FFFEE4E2', progress: 'FFF97066', border: 'FFF04438' },
  // progress = success-400 (= app bar), border = success-600
  completed: { base: 'FF32D583', progress: 'FF32D583', border: 'FF039855' },
  // milestone: fuchsia-400 (= app's .gantt_MILESTONE bg) / fuchsia-600 (border)
  milestone: { base: 'FFE879F9', progress: 'FFD946EF', border: 'FFC026D3' },
};

// Module-level shared state ensures the modal and header always read/write
// the same refs, even though both call useExportGanttChart() independently.
const is_gantt_exporting = ref(false);
// Each column object mirrors a getGanttColumns() child: { name, label, type, align, width, min_width }
const selected_columns = ref([]);

export function useExportGanttChart() {
  const project_management_store = useProjectManagementStore();
  const common_store = useCommonStore();
  const { $g, active_view } = storeToRefs(project_management_store);
  const { is_critical_activity } = project_management_store;
  const { getColumnTemplate } = useColumnTemplates();

  const active_schedule = computed(() => project_management_store.active_schedule);
  const schedule_name = computed(() => active_schedule.value?.name);

  function setGanttExporting(value) {
    is_gantt_exporting.value = value;
  }

  function cancelGanttExport() {
    setGanttExporting(false);
  }

  function setSelectedColumns(cols) {
    selected_columns.value = cols;
  }

  // Convert a Gantt column's pixel width to an Excel character-width unit.
  function getExcelWidth(col) {
    const px = col.width || col.min_width || 100;
    return Math.max(8, Math.ceil(px / 7));
  }

  // Columns whose getColumnTemplate returns HTML (avatars, icons, tooltips) rather than plain text.
  // For these we maintain simple plain-text equivalents in getColValuePlainText below.
  // If a new column is added to pm-column-templates.composable.js and its template returns HTML, add its name here and handle it in getColValuePlainText. Columns that return plain text need no changes because getColValue will delegate to getColumnTemplate automatically.
  const HTML_COLUMNS = new Set(['wbs', 'text', 'resources', 'accountable', 'weight', 'overall_weight', 'absolute_work_rate']);

  function getColValuePlainText(col, task) {
    const { name } = col;
    const g = $g.value;

    if (task.type === g.config.types.surrogate)
      return '';

    switch (name) {
      case 'wbs':
        return g.getWBSCode(task);

      case 'text':
        return task.text || task.name || '';

      case 'resources': {
        const resources = (task.resources || [])
          .map(uid => active_schedule.value?.resources?.find(r => r.uid === uid))
          .filter(Boolean)
          .map(r => r.type === 'custom' ? r.name : (common_store.get_user_or_team_name(r.external_id) || r.name || r.external_id));
        return resources.join(', ');
      }

      case 'accountable': {
        const uids = Object.keys(task.accountable || {}).filter(
          uid => common_store.is_type_user(uid) || common_store.is_type_team(uid),
        );
        return uids.map(uid => common_store.get_user_or_team_name(uid) || uid).join(', ');
      }

      case 'weight':
      case 'overall_weight':
        return isNumber(task[name]) ? `${task[name]}%` : '-';

      case 'absolute_work_rate': {
        const num = Number(task?.absolute_work_rate);
        if (task?.absolute_work_rate == null || Number.isNaN(num))
          return '';
        return Math.abs(num).toFixed(2);
      }

      default:
        return '';
    }
  }

  // For columns that return plain text from getColumnTemplate, delegate directly.
  // For columns that return HTML, use the simple plain-text implementation above.
  function getColValue(col, task) {
    if (HTML_COLUMNS.has(col.name))
      return getColValuePlainText(col, task);
    const result = getColumnTemplate(col)(task);
    return htmlToText(String(result ?? ''));
  }

  function createFill(argb) {
    return { type: 'pattern', pattern: 'solid', fgColor: { argb } };
  }

  function createBorder(argb = 'FFD0D5DD') {
    const side = { style: 'thin', color: { argb } };
    return { top: side, bottom: side, left: side, right: side };
  }

  function getTaskColors(task) {
    if (task.progress === 1)
      return GANTT_COLORS.completed;
    if (is_critical_activity(task))
      return GANTT_COLORS.critical;
    if (task._original_type === 'WBS')
      return GANTT_COLORS.wbs;
    if (task.type === $g.value.config.types.milestone)
      return GANTT_COLORS.milestone;
    return GANTT_COLORS.task;
  }

  function getScheduleDays() {
    let min_date = null;
    let max_date = null;
    $g.value.eachTask((task) => {
      if (!task.start_date || !task.end_date)
        return;
      const s = dayjs(task.start_date);
      const e = dayjs(task.end_date);
      if (!min_date || s.isBefore(min_date))
        min_date = s;
      if (!max_date || e.isAfter(max_date))
        max_date = e;
    });
    if (!min_date || !max_date)
      return [];
    const days = [];
    let cur = min_date.startOf('day');
    const end = max_date.startOf('day');
    while (cur.isSameOrBefore(end)) {
      days.push(cur);
      cur = cur.add(1, 'day');
    }
    return days;
  }

  function buildMonthGroups(all_days, fixed_col_count) {
    const months = [];
    all_days.forEach((day, i) => {
      const key = day.format('MMM YYYY');
      const last = months[months.length - 1];
      if (!last || last.key !== key)
        months.push({ key, start_col: fixed_col_count + i + 1, end_col: fixed_col_count + i + 1 });
      else
        last.end_col = fixed_col_count + i + 1;
    });
    return months;
  }

  function setupHeaderRows(worksheet, all_days, active_cols) {
    const fixed_col_count = active_cols.length;
    const month_groups = buildMonthGroups(all_days, fixed_col_count);

    // ── Row 1: fixed column labels (merged over rows 1-2) + month group labels ──
    active_cols.forEach((col, i) => {
      worksheet.mergeCells(1, i + 1, 2, i + 1);
      const cell = worksheet.getRow(1).getCell(i + 1);
      cell.value = col.label;
      cell.style = {
        font: { bold: true, size: 9, color: { argb: 'FF344054' } },
        fill: createFill('FFf2f4f7'),
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        border: createBorder('FFEAECF0'),
      };
    });

    // Alternating header colors so months are easy to distinguish
    const MONTH_COLORS = ['FF1570EF', 'FF0057D2', 'FF1849A9'];
    month_groups.forEach((month, idx) => {
      if (month.end_col > month.start_col)
        worksheet.mergeCells(1, month.start_col, 1, month.end_col);
      const cell = worksheet.getRow(1).getCell(month.start_col);
      cell.value = month.key;
      cell.style = {
        font: { bold: true, size: 9, color: { argb: 'FFFFFFFF' } },
        fill: createFill(MONTH_COLORS[idx % MONTH_COLORS.length]),
        alignment: { horizontal: 'center', vertical: 'middle' },
      };
    });

    // ── Row 2: day-number labels ──
    const day_row = worksheet.getRow(2);
    day_row.height = 14;
    all_days.forEach((day, i) => {
      const cell = day_row.getCell(fixed_col_count + i + 1);
      const is_weekend = day.day() === 0 || day.day() === 6;
      cell.value = day.date();
      cell.style = {
        font: { size: 7, color: { argb: is_weekend ? 'FF98A2B3' : 'FF344054' } },
        fill: createFill(is_weekend ? 'FFEAECF0' : 'FFf9fafb'),
        alignment: { horizontal: 'center', vertical: 'middle' },
      };
    });

    worksheet.getRow(1).height = 22;
  }

  async function exportGanttChart() {
    try {
      const ExcelJS = await import('exceljs');
      const { saveAs } = await import('file-saver');

      const all_days = getScheduleDays();
      if (!all_days.length) {
        setGanttExporting(false);
        return;
      }

      const active_cols = selected_columns.value;
      const fixed_col_count = active_cols.length;

      const LOCKABLE_COLS = new Set(['wbs', 'text', 'id']);
      const frozen_col_count = active_cols.filter(c => LOCKABLE_COLS.has(c.name)).length;

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Gantt Chart', {
        views: [{ state: 'frozen', xSplit: frozen_col_count, ySplit: 2, showGridLines: false }],
      });
      worksheet.properties.outlineProperties = { summaryBelow: false };

      // Column widths
      worksheet.columns = [
        ...active_cols.map(c => ({ width: getExcelWidth(c) })),
        ...all_days.map(() => ({ width: 3 })),
      ];

      setupHeaderRows(worksheet, all_days, active_cols);

      $g.value.eachTask((task) => {
        const row = worksheet.addRow([]);
        row.height = 16;
        // Excel supports a maximum of 8 nesting levels (outlineLevel 0–7).
        // If the schedule has more, skip nesting entirely to avoid a corrupt file.
        if (active_view.value.data.wbs_level_max <= 8)
          row.outlineLevel = Math.max(0, $g.value.calculateTaskLevel(task) - 1);

        const is_parent = ![$g.value.config.types.task, $g.value.config.types.milestone].includes(task.type);

        active_cols.forEach((col, idx) => {
          row.getCell(idx + 1).value = getColValue(col, task);
        });

        const fixed_fill = is_parent ? 'FFEAECF0' : 'FFFFFFFF';
        for (let c = 1; c <= fixed_col_count; c++) {
          row.getCell(c).style = {
            font: { bold: is_parent, size: 9, color: { argb: 'FF344054' } },
            fill: createFill(fixed_fill),
            alignment: {
              horizontal: active_cols[c - 1].align || 'left',
              vertical: 'middle',
            },
            border: createBorder('FFEAECF0'),
          };
        }

        if (!task.start_date || !task.end_date) {
          row.commit();
          return;
        }

        const colors = getTaskColors(task);
        const task_start = dayjs(task.start_date).startOf('day');
        const task_end = dayjs(task.end_date).startOf('day');

        // Milestones: render a single diamond marker cell at the milestone date.
        // (end_date is always start_date + 1 day after preprocessing, so the bar
        //  loop would produce one cell but without a visible distinction we handle
        //  milestones here to show a ◆ symbol and the correct fuchsia/critical/green color.)
        if (task.type === $g.value.config.types.milestone) {
          const day_idx = all_days.findIndex(d => d.isSame(task_start, 'day'));
          if (day_idx !== -1) {
            const border_side = { style: 'thin', color: { argb: colors.border } };
            const cell = row.getCell(fixed_col_count + day_idx + 1);
            cell.value = '◆';
            cell.style = {
              fill: createFill(colors.progress),
              font: { size: 7, bold: true, color: { argb: 'FFFFFFFF' } },
              alignment: { horizontal: 'center', vertical: 'middle' },
              border: { top: border_side, bottom: border_side, left: border_side, right: border_side },
            };
          }
          row.commit();
          return;
        }

        const total_days = task_end.diff(task_start, 'day');
        const progress_days = Math.round(total_days * (task.progress || 0));
        const progress_cutoff = task_start.add(progress_days, 'day');

        // Collect bar cells so we can apply border logic after
        const bar_cells = [];
        all_days.forEach((day, i) => {
          if (day.isBefore(task_start) || !day.isBefore(task_end))
            return;
          const is_done = day.isBefore(progress_cutoff);
          bar_cells.push({ col: fixed_col_count + i + 1, argb: is_done ? colors.progress : colors.base, is_done });
        });

        const border_side = { style: 'thin', color: { argb: colors.border } };

        bar_cells.forEach((bar_cell, idx) => {
          const is_first = idx === 0;
          const is_last = idx === bar_cells.length - 1;
          // progress → base transition: draw a separator on the right edge of the last done cell
          const is_progress_end = bar_cell.is_done && (!bar_cells[idx + 1]?.is_done || is_last);

          row.getCell(bar_cell.col).style = {
            fill: createFill(bar_cell.argb),
            border: {
              top: border_side,
              bottom: border_side,
              ...(is_first ? { left: border_side } : {}),
              ...(is_last ? { right: border_side } : {}),
              ...(is_progress_end && !is_last ? { right: border_side } : {}),
            },
          };
        });

        row.commit();
      });

      if (is_gantt_exporting.value) {
        const slugified = slugify(schedule_name.value || 'schedule', { lower: true, strict: true });
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `${slugified}-gantt-chart.xlsx`);
        setTimeout(() => setGanttExporting(false), 3000);
      }
    }
    catch (err) {
      logger.error(err);
      setGanttExporting(false);
    }
  }

  return {
    is_gantt_exporting,
    setGanttExporting,
    setSelectedColumns,
    cancelGanttExport,
    exportGanttChart,
  };
}
